import { sortBy } from "lodash"
import { createSelector } from "reselect"
import { Concept } from "../../models"
import { ConceptFilter, ConceptsInfo } from "./index"

const getAllConcepts = (state: ConceptsInfo): Concept[] => state.concepts
const getPageSize = (state: ConceptsInfo): number => state.paging.pageSize
const getFilter = (state: ConceptsInfo): ConceptFilter => state.filter
const getPageNumber = (state: ConceptsInfo): number => state.paging.page

export const getFilteredConcepts = createSelector(
    [getAllConcepts, getFilter],
    (concepts, filter): Concept[] => {
        const filteredByConceptScreenLabel = concepts.filter((concept) =>
            concept.conceptLabel
                .toLowerCase()
                .includes(filter.concept.label.toLowerCase()),
        )
        const filteredByConceptCode = filteredByConceptScreenLabel.filter(
            (concept) =>
                concept.conceptCode
                    .toLowerCase()
                    .includes(filter.concept.code.toLowerCase()),
        )

        const filteredByWorkspaceScreenLabel = filteredByConceptCode.filter(
            (concept) =>
                concept.workspaceLabel
                    .toLowerCase()
                    .includes(filter.workspace.label.toLowerCase()),
        )
        const filteredByWorkspaceCode = filteredByWorkspaceScreenLabel.filter(
            (concept) =>
                concept.workspaceCode
                    .toLowerCase()
                    .includes(filter.workspace.code.toLowerCase()),
        )

        const filterInstances = filteredByWorkspaceCode.filter(
            (concept) => concept.isTemplate || filter.includeInstances,
        )

        const filterActive = filterInstances.filter(
            (concept) => concept.isActive === filter.isActive,
        )

        return filterActive
    },
)

export interface VisibleConcepts {
    visibleConcepts: Concept[]
}

export const getVisibleConcepts = createSelector(
    [getFilteredConcepts, getPageNumber, getPageSize],
    (filteredConcepts, pageNumber, pageSize): Concept[] => {
        const sortedConcepts = sortBy(filteredConcepts, [
            "workspaceLabel",
            "conceptLabel",
        ])
        const pagedConcepts = sortedConcepts.slice(
            0,
            pageSize * (pageNumber + 1),
        )
        return pagedConcepts
    },
)

export interface HasMoreVisibleConcepts {
    hasMore: boolean
}

export const getHasMoreVisibleConcepts = createSelector(
    [getFilteredConcepts, getVisibleConcepts],
    (filteredConcepts, visibleConcepts): boolean =>
        filteredConcepts.length > visibleConcepts.length,
)

export interface NextPageSize {
    nextPageSize: number
}

export const getNextPageSize = createSelector(
    [getFilteredConcepts, getVisibleConcepts, getPageSize],
    (filteredConcepts, visibleConcepts, pageSize): number => {
        if (visibleConcepts.length === filteredConcepts.length) {
            return 0
        }
        if (filteredConcepts.length - visibleConcepts.length < pageSize) {
            return filteredConcepts.length - visibleConcepts.length
        }
        return pageSize
    },
)
